import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CommercialCustomerFormValues,
  commercialCustomerRequestSchema,
  commercialPotentialBuyerSchema,
  commercialPotentialBuyerSubmitSchema,
} from '../data/CommercialCustomerFormValues';
import RestHttpClient, { renderValidationErrors } from '../../../common/RestHttpClient';
import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  CommercialCustomerForm,
  commercialEntryFields,
  commercialPartnerFields,
  errorHandler,
} from '../form/CommercialCustomerForm';
import { LoadingModal } from '../../../component/LoadingModal';
import { Box, Button } from '@mui/material';
import { CommercialCustomerViewApiResult, LocalApiMessage } from '../../../generated/ApiClient';
import { useTranslation } from 'react-i18next';
import { FormCheckbox } from '../../../component/form/FormCheckbox';
import { PotentialCommercialCustomerForm } from '../potential/PotentialCommercialCustomerForm';
import { removeNullValues } from '../../../common/validation/Validations';
import { austriaCountryId, noPepId } from '../data/CustomerConstants';

interface Props {
  customerId?: number;
  onSubmit: (
    values: CommercialCustomerFormValues,
    isDirty: boolean,
    submitType?: string,
    callback?: Function,
    hasRelevantChanges?: boolean
  ) => unknown;
  overrideValues?: any;
  readonly: boolean;
  actions?: JSX.Element;
  customerPromise?: () => Promise<CommercialCustomerViewApiResult>;
  errors?: LocalApiMessage[];
  isNew?: boolean;
  noPotential?: boolean;
  setUnsavedChanges?: Dispatch<SetStateAction<boolean>>;
  showSelfDisclosure?: boolean;
  onIdentityCheckResolved?: () => void;
}

const defaultValues = {
  addressCountryId: austriaCountryId,
  isPotentialBuyer: false,
  pepSpecificationId: noPepId,
  dataInformationSheetTransferred: false,
  riskIncreasingCircumstances: false,
};

export function EditCommercialCustomerForm({
  customerId,
  onSubmit,
  overrideValues,
  readonly,
  actions,
  customerPromise,
  errors,
  isNew,
  noPotential,
  setUnsavedChanges,
  showSelfDisclosure,
  onIdentityCheckResolved,
}: Props) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [tabErrors, setTabErrors] = useState<number[]>([]);
  const [hasRelevantChanges, setHasRelevantChanges] = useState<boolean>(false);

  const [initialPotential, setInitialPotential] = useState(false);

  const {
    control,
    handleSubmit,
    trigger,
    formState,
    reset,
    getValues,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm<CommercialCustomerFormValues>({
    resolver: async (data, context, options) => {
      if (options.names && options.names.length === 1) {
        if (!options.fields['selfDisclosureAgreed'] && !options.fields['ksvSearchDone']) {
          setValue('selfDisclosureAgreed', false);
        }

        if (!options.fields['ksvSearchDone']) {
          setHasRelevantChanges(true);
        }
      }

      if (data.isPotentialBuyer) {
        return zodResolver(commercialPotentialBuyerSubmitSchema)(
          removeNullValues(data),
          context,
          options
        );
      }
      return zodResolver(commercialCustomerRequestSchema)(removeNullValues(data), context, options);
    },
    mode: 'onChange',
    defaultValues: defaultValues,
  });

  const handleSetInitialized = () => setTimeout(() => setInitialized(true), 500);

  const checkShowAllForPotential = (d: any) => {
    if (!commercialPotentialBuyerSchema.safeParse(removeNullValues(d)).success) {
      setShowAll(true);
      setValue('isPotentialBuyer', true);
    } else {
      setShowAll(false);
      setValue('isPotentialBuyer', false);
    }
    setInitialPotential(d.isPotentialBuyer);
  };

  const setErrors = () => {
    const includesFields = (fields: Array<keyof CommercialCustomerFormValues>) => {
      if (!errors) {
        return false;
      }
      for (const e of errors) {
        // @ts-ignore
        if (fields.includes(e.target)) {
          return true;
        }
      }
      return false;
    };
    clearErrors();
    setTabErrors([]);
    if (errors && errors.length) {
      errors.forEach((e) => {
        // @ts-ignore
        setError(e.target, { type: 'custom', message: t('deals.edit.creditCheck.required') });
      });

      if (includesFields(commercialEntryFields)) {
        setTabErrors((prevState) => [...prevState, 0]);
      }
      if (includesFields(commercialPartnerFields)) {
        setTabErrors((prevState) => [...prevState, 1]);
      }
    }
  };

  useEffect(() => {
    setErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const fetch = async () => {
    let customer: Promise<CommercialCustomerViewApiResult> | undefined;

    setInitialized(false);
    if (customerPromise) {
      customer = customerPromise();
    } else if (customerId && !isNaN(customerId)) {
      customer = RestHttpClient.getCommercialCustomer(customerId);
    }

    if (customer) {
      setLoading(true);
      customer
        .then((res) => {
          if (overrideValues) {
            const resetValues = { ...res.data, ...overrideValues };
            checkShowAllForPotential(resetValues);
            reset(resetValues);
          } else {
            checkShowAllForPotential(res.data);
            reset({ ...res.data, phoneNumber: res.data.phoneNumber ?? undefined });
          }
          setErrors();
        })
        .finally(() => {
          setLoading(false);
          handleSetInitialized();
        });
    } else {
      if (overrideValues) {
        checkShowAllForPotential(overrideValues);
        reset({ ...overrideValues });
      } else {
        reset(defaultValues);
      }
      setErrors();
      handleSetInitialized();
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line
  }, [customerId]);

  useEffect(() => {
    if (isNew && (!overrideValues || overrideValues === {})) {
      reset(defaultValues);
      setErrors();
    }
    // eslint-disable-next-line
  }, [isNew]);

  const handleFormSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const valid = await trigger();
    if (!valid) return;

    const changesMade = Object.keys(formState.dirtyFields).length > 0;
    // @ts-ignore
    const submitType = event.nativeEvent.submitter.name;
    setTabErrors([]);
    await handleSubmit(
      async (values) => {
        try {
          await onSubmit(
            values,
            changesMade,
            submitType,
            () => {
              checkShowAllForPotential(values);
              if (setUnsavedChanges) setUnsavedChanges(false);
            },
            hasRelevantChanges
          );
          if (changesMade) {
            reset(defaultValues, { keepValues: true });
            setHasRelevantChanges(false);
          }
        } catch (error: any) {
          renderValidationErrors(error, setError);
        }
      },
      (err) => {
        errorHandler(err, setTabErrors);
      }
    )(event);
  };

  const isPotential = watch('isPotentialBuyer');
  const ksvDoneError = Boolean(formState.errors.ksvSearchDone?.message);

  useEffect(() => {
    const subscription = watch((_) => {
      if (setUnsavedChanges && initialized) {
        setUnsavedChanges(true);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, initialized]);

  return (
    <form id="edit-customer-form" onSubmit={handleFormSubmit} noValidate>
      <LoadingModal loading={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 1,
        }}
      >
        <Box>
          {((isNew && !noPotential) || initialPotential) && (
            <FormCheckbox
              variant="switch"
              control={control}
              label={t('customers.isPotentialBuyer')}
              name="isPotentialBuyer"
              inverse
              disabled={readonly}
            />
          )}
        </Box>

      </Box>

      {isPotential && !showAll ? (
        <PotentialCommercialCustomerForm control={control} isNew={isNew} actions={actions} />
      ) : (
        <CommercialCustomerForm
          control={control}
          setValue={setValue}
          actions={actions}
          isNew={isNew}
          readonly={readonly}
          tabErrors={tabErrors}
          showSelfDisclosure={showSelfDisclosure}
        />
      )}

      {isPotential && (
        <Box>
          {showAll ? (
            <Button onClick={() => setShowAll(false)}>{t('customers.showMinimal')}</Button>
          ) : (
            <Button onClick={() => setShowAll(true)}>{t('customers.showAll')}</Button>
          )}
        </Box>
      )}
    </form>
  );
}
